import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Users, GraduationCap, Lock, Sparkles, ArrowRight, CheckCircle2, BookOpen } from "lucide-react";

const TeacherDashboard = () => {
  const [classes, setClasses] = useState([]);
  const [isPaid, setIsPaid] = useState(false);
  const [totalStudents, setTotalStudents] = useState(0);
  const [cap, setCap] = useState(30);
  const [unlockedUntil, setUnlockedUntil] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", subject: "", grade_band: "" });
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const load = async () => {
    try {
      const r = await api.get("/teacher/classes");
      setClasses(r.data.classes || []);
      setIsPaid(!!r.data.is_paid);
      setTotalStudents(r.data.total_students || 0);
      setCap(r.data.free_student_cap || 30);
      setUnlockedUntil(r.data.teacher_unlocked_until || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load classes");
    }
  };

  useEffect(() => { load(); }, []);

  // Reconcile ?teacher_unlock=success from Stripe redirect
  useEffect(() => {
    const status = params.get("teacher_unlock");
    const sid = params.get("session_id");
    if (status === "success" && sid) {
      api.get(`/teacher/upgrade/status?session_id=${encodeURIComponent(sid)}`)
        .then((r) => { if (r.data.paid) { toast.success("Teacher unlock activated!"); load(); } })
        .catch(() => {});
    }
    if (status === "cancelled") toast("Upgrade cancelled — no charge made.");
    // eslint-disable-next-line
  }, []);

  const createClass = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Please name your class"); return; }
    setCreating(true);
    try {
      const r = await api.post("/teacher/classes", form);
      toast.success(`"${r.data.class.name}" created — 30 anonymous accounts ready.`);
      setShowCreate(false);
      setForm({ name: "", subject: "", grade_band: "" });
      await load();
      navigate(`/teacher/classes/${r.data.class.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create class");
    } finally {
      setCreating(false);
    }
  };

  const startUpgrade = async () => {
    try {
      const r = await api.post("/teacher/upgrade/checkout");
      window.location.href = r.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start upgrade");
    }
  };

  const atCap = !isPaid && totalStudents >= cap;

  return (
    <div className="min-h-screen bg-mm-parchment bg-topo">
      <Navbar />
      <div className="container mx-auto py-10 max-w-5xl">
        <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">Trail Leader HQ</div>
        <div className="mt-2 flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black text-mm-navy">Your classes</h1>
            <p className="text-mm-navy/70 mt-2 max-w-2xl text-sm">
              Each class gives you 30 anonymous student accounts — zero personal data collected. Perfect for classrooms.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/teacher/setup-guide" className="btn-trail btn-outline-navy text-sm" data-testid="teacher-setup-guide-link">
              <BookOpen size={14} strokeWidth={2.5} /> Setup guide
            </Link>
            <Link to="/student/login-guide" target="_blank" rel="noopener" className="btn-trail btn-outline-navy text-sm" data-testid="teacher-student-guide-link">
              <BookOpen size={14} strokeWidth={2.5} /> Student login guide
            </Link>
            <button
              onClick={() => atCap ? null : setShowCreate(true)}
              disabled={atCap}
              className="btn-trail btn-gold text-sm disabled:opacity-50"
              data-testid="teacher-create-class-btn"
            >
              <Plus size={16} strokeWidth={2.5} /> New class
            </button>
          </div>
        </div>

        {/* First-time onboarding banner (only when no classes yet) */}
        {classes.length === 0 && (
          <div className="mt-6 rounded-2xl border-2 border-mm-gold/50 bg-mm-gold/10 p-5 flex items-center justify-between gap-3 flex-wrap" data-testid="teacher-setup-banner">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-mm-gold text-mm-navy flex items-center justify-center shrink-0">
                <BookOpen size={20} strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-heading text-lg font-black text-mm-navy">New here? Start with the setup guide.</div>
                <p className="text-sm text-mm-navy/80 mt-0.5">
                  A step-by-step walkthrough (in plain English) for setting up your class, sharing logins, and pushing your first expedition.
                </p>
              </div>
            </div>
            <Link to="/teacher/setup-guide" className="btn-trail btn-gold text-sm shrink-0" data-testid="teacher-setup-banner-cta">
              Read the guide <ArrowRight size={14} strokeWidth={2.5} />
            </Link>
          </div>
        )}

        {/* Privacy note */}
        <div className="mt-6 rounded-xl border-2 border-mm-steel/20 bg-white p-4 text-sm text-mm-navy/85" data-testid="teacher-privacy-note">
          <Lock size={14} className="inline mr-1 text-mm-gold" strokeWidth={2.5} />
          These accounts are fully anonymous. No student personal data is collected or stored.
        </div>

        {atCap && (
          <div className="mt-4 rounded-xl border-2 border-mm-gold/40 bg-mm-gold/10 p-4 flex items-start justify-between gap-3" data-testid="teacher-upgrade-prompt">
            <div className="flex items-start gap-2">
              <Sparkles size={16} strokeWidth={2.5} className="text-mm-navy mt-0.5" />
              <div>
                <div className="font-heading font-bold text-mm-navy">Need more than 30 student slots?</div>
                <p className="text-sm text-mm-navy/85 mt-0.5">
                  Upgrade to Teacher Premium — <b>$100 for one school year</b> of unlimited classes.
                </p>
              </div>
            </div>
            <button onClick={startUpgrade} className="btn-trail btn-gold text-sm shrink-0" data-testid="teacher-upgrade-btn">
              Unlock — $100/yr
            </button>
          </div>
        )}

        {isPaid && (
          <div className="mt-4 rounded-xl border-2 border-mm-steel/30 bg-mm-steel/5 p-4 flex items-center gap-2 text-sm text-mm-navy" data-testid="teacher-paid-banner">
            <CheckCircle2 size={16} strokeWidth={2.5} className="text-mm-steel" />
            Teacher Premium is active — unlimited classes.
            {unlockedUntil && (
              <span className="text-mm-navy/60 ml-1" data-testid="teacher-unlock-until">
                Renews on {new Date(unlockedUntil).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}.
              </span>
            )}
          </div>
        )}

        {/* Class list */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {classes.length === 0 && (
            <div className="col-span-full text-center py-16 border-2 border-dashed border-mm-navy/15 rounded-xl text-mm-navy/60">
              No classes yet. Create your first classroom to get started.
            </div>
          )}
          {classes.map((c) => (
            <Link key={c.id} to={`/teacher/classes/${c.id}`} className="stage-card p-5 hover:border-mm-gold" data-testid={`teacher-class-card-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-heading text-xl font-black text-mm-navy">{c.name}</div>
                  <div className="text-xs text-mm-navy/60 mt-0.5">
                    {c.subject || "General"}{c.grade_band ? ` · Grade ${c.grade_band}` : ""}
                  </div>
                </div>
                <GraduationCap size={22} strokeWidth={2} className="text-mm-steel shrink-0" />
              </div>
              <div className="mt-4 flex items-center justify-between">
                <div className="flex items-center gap-1 text-sm text-mm-navy/80">
                  <Users size={14} strokeWidth={2.5} /> {c.student_count} slots
                </div>
                <ArrowRight size={14} strokeWidth={2.5} className="text-mm-steel" />
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Create class dialog */}
      {showCreate && (
        <div className="fixed inset-0 z-50 bg-mm-navy/50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={createClass}
            className="w-full max-w-md bg-white rounded-2xl p-6 border-2 border-mm-navy/10 space-y-3"
            data-testid="create-class-form"
          >
            <div className="text-xs uppercase tracking-widest font-bold text-mm-steel">New class</div>
            <h3 className="font-heading text-2xl font-black text-mm-navy">Set up your classroom</h3>
            <p className="text-xs text-mm-navy/70">30 anonymous student accounts will be generated automatically. You can rename them with private nicknames from the roster.</p>
            <Field label="Class name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Period 3 Science" testid="new-class-name" />
            <Field label="Subject (optional)" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} placeholder="Life Science" testid="new-class-subject" />
            <Field label="Grade level (optional)" value={form.grade_band} onChange={(v) => setForm({ ...form, grade_band: v })} placeholder="5" testid="new-class-grade" />
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => setShowCreate(false)} className="btn-trail btn-outline-navy text-sm flex-1">Cancel</button>
              <button type="submit" disabled={creating} className="btn-trail btn-gold text-sm flex-1 disabled:opacity-50" data-testid="create-class-submit">
                {creating ? "Creating..." : "Create class"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, placeholder, testid }) => (
  <div>
    <label className="text-xs uppercase tracking-widest font-bold text-mm-steel">{label}</label>
    <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="mt-1.5 w-full bg-white border-2 border-mm-navy/15 rounded-lg px-4 py-3 focus:outline-none focus:border-mm-steel"
      data-testid={testid} />
  </div>
);

export default TeacherDashboard;
